import { CalendarClock, Clock3, Info } from "lucide-react";
import { useMemo } from "react";
import clsx from "clsx";

import { useQuery } from "@tanstack/react-query";
import type { CoachAvailability } from "@flexv-coach/shared";
import { WEEK_DAYS } from "@flexv-coach/shared";
import { Alert } from "@/components/ui/Alert";
import { Badge } from "@/components/ui/Badge";
import { Card, CardContent, CardHeader, StatsCard } from "@/components/ui/Card";
import { EmptyState, ErrorState, LoadingBlock } from "@/components/ui/States";
import { useI18n } from "@/hooks/useI18n";
import { coachApi } from "@/lib/api";
import { formatDayMonth, formatTimeRange, minutesBetween } from "@/lib/date";

interface DayColumn {
    /** 1 = Monday .. 7 = Sunday (mengikuti `day_of_week` DB). */
    value: number;
    label: string;
    windows: CoachAvailability[];
    activeWindows: CoachAvailability[];
}

interface OutletGroup {
    outletId: string;
    outletName: string;
    days: DayColumn[];
    totalMinutes: number;
    activeDays: number;
    windowCount: number;
}

/**
 * Menu Ketersediaan — tampilan baca (read-only) atas aturan mingguan coach.
 *
 * Data mengikuti tabel `coach_availabilities` milik core: `day_of_week` 1–7,
 * `start/end_time` bertipe `time`, `is_available`, plus rentang
 * `effective_from`/`effective_until`. Aturannya sendiri dikelola admin outlet,
 * jadi halaman ini tidak menyediakan form ubah — hanya visualisasi.
 */
export function AvailabilityPage() {
    const { t } = useI18n();

    const { data, isPending, isError, error, refetch } = useQuery({
        queryKey: ["coach-availability"],
        queryFn: ({ signal }) => coachApi.availability(signal),
    });

    const groups = useMemo(() => groupByOutlet(data ?? []), [data]);

    const totals = useMemo(() => {
        let minutes = 0;
        let windows = 0;
        const days = new Set<string>();
        for (const group of groups) {
            minutes += group.totalMinutes;
            windows += group.windowCount;
            for (const day of group.days) {
                if (day.activeWindows.length > 0) days.add(`${group.outletId}:${day.value}`);
            }
        }
        return { minutes, windows, days: days.size };
    }, [groups]);

    return (
        <div className="flex flex-col gap-6">
            <header>
                <h1 className="app-text-primary text-2xl font-bold sm:text-3xl">
                    {t("availability.title")}
                </h1>
                <p className="app-shell-text-muted mt-1 text-sm">{t("availability.subtitle")}</p>
            </header>

            <Alert tone="info">
                <span className="inline-flex items-start gap-2">
                    <Info className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{t("availability.readOnlyNote")}</span>
                </span>
            </Alert>

            {isPending && <LoadingBlock label={t("availability.loading")} />}

            {isError && (
                <ErrorState
                    message={error instanceof Error ? error.message : t("availability.loadFailed")}
                    onRetry={() => void refetch()}
                />
            )}

            {data && groups.length === 0 && (
                <Card>
                    <EmptyState
                        icon={<Clock3 className="h-6 w-6" />}
                        title={t("availability.empty.title")}
                        description={t("availability.empty.description")}
                    />
                </Card>
            )}

            {groups.length > 0 && (
                <section className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <StatsCard
                        label={t("availability.summary.totalHours")}
                        value={formatWeeklyHours(totals.minutes)}
                        icon={<Clock3 className="h-5 w-5" />}
                        hint={t("availability.summary.hoursPerWeek", {
                            h: formatWeeklyHours(totals.minutes),
                        })}
                    />
                    <StatsCard
                        label={t("availability.summary.daysActive", { n: totals.days })}
                        value={totals.days}
                        icon={<CalendarClock className="h-5 w-5" />}
                        hint={t("availability.summary.windows", { n: totals.windows })}
                    />
                    <StatsCard
                        label={t("availability.summary.windows", { n: totals.windows })}
                        value={totals.windows}
                        icon={<Clock3 className="h-5 w-5" />}
                        hint={t("availability.summary.hoursPerWeek", {
                            h: formatWeeklyHours(totals.minutes),
                        })}
                    />
                </section>
            )}

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                {groups.map((group) => (
                    <Card key={group.outletId}>
                        <CardHeader
                            title={group.outletName}
                            description={t("availability.summary.hoursPerWeek", {
                                h: formatWeeklyHours(group.totalMinutes),
                            })}
                            icon={<Clock3 className="h-5 w-5" />}
                            action={
                                <Badge tone="neutral">
                                    {t("availability.summary.daysActive", { n: group.activeDays })}
                                </Badge>
                            }
                        />
                        <CardContent className="flex flex-col gap-2" noPadding>
                            <ul className="flex flex-col">
                                {group.days.map((day) => (
                                    <li
                                        key={day.value}
                                        className="flex items-start gap-3 border-b app-shell-border px-5 py-3 last:border-b-0"
                                    >
                                        <p
                                            className={clsx(
                                                "w-12 shrink-0 text-sm font-semibold",
                                                day.activeWindows.length > 0
                                                    ? "app-text-primary"
                                                    : "app-text-soft",
                                            )}
                                        >
                                            {day.label}
                                        </p>

                                        {day.windows.length === 0 ? (
                                            <p className="app-text-soft text-sm">
                                                {t("availability.closed")}
                                            </p>
                                        ) : (
                                            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                                                {day.windows.map((window) => (
                                                    <WindowRow key={window.id} window={window} t={t} />
                                                ))}
                                            </div>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </CardContent>
                    </Card>
                ))}
            </div>
        </div>
    );
}

function WindowRow({
    window,
    t,
}: {
    window: CoachAvailability;
    t: (key: string, params?: Record<string, string | number>) => string;
}) {
    return (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span
                className={clsx(
                    "inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-semibold",
                    window.isAvailable
                        ? "bg-[var(--color-success-bg)] text-[var(--color-success-text)]"
                        : "app-content-subtle app-shell-text-muted",
                )}
            >
                <span
                    aria-hidden
                    className={clsx(
                        "h-1.5 w-1.5 rounded-full",
                        window.isAvailable
                            ? "bg-[var(--color-success-text)]"
                            : "bg-[var(--app-text-soft)]",
                    )}
                />
                {formatTimeRange(window.startTime, window.endTime)}
            </span>

            {!window.isAvailable && (
                <span className="app-text-soft text-xs">{t("availability.unavailable")}</span>
            )}

            <EffectiveNote window={window} t={t} />
        </div>
    );
}

/** Keterangan rentang berlaku — hanya muncul bila aturannya berjangka. */
function EffectiveNote({
    window,
    t,
}: {
    window: CoachAvailability;
    t: (key: string, params?: Record<string, string | number>) => string;
}) {
    if (!window.effectiveFrom && !window.effectiveUntil) return null;

    const text = window.effectiveFrom
        ? `${t("availability.effectiveFrom", { date: formatDayMonth(window.effectiveFrom) })}${window.effectiveUntil ? ` ${t("availability.effectiveUntil", { date: formatDayMonth(window.effectiveUntil) })}` : ""}`
        : t("availability.effectiveUntil", {
              date: formatDayMonth(window.effectiveUntil ?? ""),
          });

    return <span className="app-text-soft text-xs">· {text}</span>;
}

/** Total menit → "38,5 jam" (id) / "38.5 hrs" (en) tanpa desimal bila bulat. */
function formatWeeklyHours(totalMinutes: number): string {
    const hours = totalMinutes / 60;
    return Number.isInteger(hours) ? String(hours) : hours.toFixed(1).replace(".", ",");
}

function groupByOutlet(rows: CoachAvailability[]): OutletGroup[] {
    const groups = new Map<string, Map<number, CoachAvailability[]>>();
    const names = new Map<string, string>();

    for (const row of rows) {
        if (!groups.has(row.outletId)) {
            groups.set(row.outletId, new Map());
            names.set(row.outletId, row.outlet?.name ?? "Outlet");
        }
        const byDay = groups.get(row.outletId)!;
        const bucket = byDay.get(row.dayOfWeek) ?? [];
        bucket.push(row);
        byDay.set(row.dayOfWeek, bucket);
    }

    return [...groups.entries()].map(([outletId, byDay]) => {
        const days: DayColumn[] = WEEK_DAYS.map((day) => {
            const windows = (byDay.get(day.value) ?? [])
                .slice()
                .sort((a, b) => a.startTime.localeCompare(b.startTime));
            return {
                value: day.value,
                label: day.label,
                windows,
                activeWindows: windows.filter((window) => window.isAvailable),
            };
        });

        let totalMinutes = 0;
        let activeDays = 0;
        let windowCount = 0;
        for (const day of days) {
            windowCount += day.activeWindows.length;
            if (day.activeWindows.length > 0) activeDays += 1;
            for (const window of day.activeWindows) {
                totalMinutes += minutesBetween(window.startTime, window.endTime);
            }
        }

        return {
            outletId,
            outletName: names.get(outletId) ?? "Outlet",
            days,
            totalMinutes,
            activeDays,
            windowCount,
        };
    });
}
